import { useEffect } from "react";
import { Keyboard, type KeyboardInfo } from "@capacitor/keyboard";
import type { PluginListenerHandle } from "@capacitor/core";
import { isNativeIOS } from "../lib/native-auth";

const EDITABLE_SELECTOR = [
    "input:not([type='checkbox']):not([type='radio']):not([type='range']):not([type='color'])",
    "textarea",
    "select",
    "[contenteditable='true']",
    ".ProseMirror",
].join(",");

/**
 * Publishes the visible mobile viewport and keyboard occlusion as CSS custom
 * properties. iOS keeps fixed/sticky layout attached to the layout viewport
 * while its keyboard shrinks and pans the visual viewport, so composer tools
 * need this explicit bridge to stay immediately above the keyboard.
 */
export function useKeyboardViewport(): void {
    useEffect(() => {
        const root = document.documentElement;
        if (isNativeIOS()) {
            const handles: PluginListenerHandle[] = [];
            let disposed = false;

            const syncHeight = () => {
                root.style.setProperty("--visual-viewport-height", `${Math.round(window.innerHeight)}px`);
            };
            const onShow = (info: KeyboardInfo) => {
                root.toggleAttribute("data-keyboard-open", true);
                // Capacitor resizes the body, so adding the keyboard height as
                // a bottom inset would count the occlusion twice.
                root.style.setProperty("--keyboard-inset", "0px");
                root.style.setProperty("--native-keyboard-height", `${Math.round(info.keyboardHeight)}px`);
                requestAnimationFrame(syncHeight);
            };
            const onHide = () => {
                root.removeAttribute("data-keyboard-open");
                root.style.setProperty("--keyboard-inset", "0px");
                root.style.setProperty("--native-keyboard-height", "0px");
                requestAnimationFrame(syncHeight);
            };

            void Promise.all([
                Keyboard.addListener("keyboardWillShow", onShow),
                Keyboard.addListener("keyboardWillHide", onHide),
            ]).then(registered => {
                if (disposed) {
                    for (const handle of registered) void handle.remove();
                } else {
                    handles.push(...registered);
                }
            });
            window.addEventListener("resize", syncHeight);
            syncHeight();

            return () => {
                disposed = true;
                for (const handle of handles) void handle.remove();
                window.removeEventListener("resize", syncHeight);
                root.style.removeProperty("--visual-viewport-height");
                root.style.removeProperty("--keyboard-inset");
                root.style.removeProperty("--native-keyboard-height");
                root.removeAttribute("data-keyboard-open");
            };
        }

        const viewport = window.visualViewport;
        let frame = 0;
        let delayedSync = 0;
        let layoutHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);

        const isEditing = () => {
            const active = document.activeElement;
            return active instanceof HTMLElement && Boolean(active.closest(EDITABLE_SELECTOR));
        };

        const apply = () => {
            frame = 0;
            const editing = isEditing();
            const currentLayoutHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
            if (!editing) layoutHeight = currentLayoutHeight;

            const visibleHeight = viewport?.height ?? window.innerHeight;
            const visibleOffsetTop = viewport?.offsetTop ?? 0;
            const occludedBottom = Math.max(0, layoutHeight - visibleHeight - visibleOffsetTop);
            // Browser toolbars can account for a small visual/layout delta.
            // Only treat a focused editable + a material reduction as a keyboard.
            const keyboardInset = editing && occludedBottom > 80 ? occludedBottom : 0;

            root.style.setProperty("--visual-viewport-height", `${Math.round(visibleHeight)}px`);
            root.style.setProperty("--keyboard-inset", `${Math.round(keyboardInset)}px`);
            root.toggleAttribute("data-keyboard-open", keyboardInset > 0);
        };

        const schedule = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(apply);
        };

        const focusSync = () => {
            schedule();
            window.clearTimeout(delayedSync);
            // Safari can publish its final visual viewport only after the
            // keyboard animation has completed.
            delayedSync = window.setTimeout(schedule, 420);
        };

        viewport?.addEventListener("resize", schedule);
        viewport?.addEventListener("scroll", schedule);
        window.addEventListener("resize", schedule);
        document.addEventListener("focusin", focusSync);
        document.addEventListener("focusout", focusSync);
        schedule();

        return () => {
            viewport?.removeEventListener("resize", schedule);
            viewport?.removeEventListener("scroll", schedule);
            window.removeEventListener("resize", schedule);
            document.removeEventListener("focusin", focusSync);
            document.removeEventListener("focusout", focusSync);
            if (frame) cancelAnimationFrame(frame);
            window.clearTimeout(delayedSync);
            root.style.removeProperty("--visual-viewport-height");
            root.style.removeProperty("--keyboard-inset");
            root.removeAttribute("data-keyboard-open");
        };
    }, []);
}
